import React, { useState, useEffect } from 'react';
import { X, Building2, User, MapPin, Phone, Mail, Globe, FileText, Hash, Upload, Download, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { countryService, Country } from '../../services/country.service';

// Country-specific address configuration
type TaxIdKey = 'businessNumber' | 'gstNumber' | 'qstNumber' | 'sin';
interface TaxIdField { key: TaxIdKey; label: string; placeholder: string; individualOnly?: boolean; }
const COUNTRY_CONFIG: Record<string, {
  regionLabel: string;
  regions: { value: string; label: string }[];
  postalLabel: string;
  postalPlaceholder: string;
  currency: string;
  taxIdFields: TaxIdField[];
}> = {
  canada: {
    regionLabel: 'Province',
    regions: [
      { value: 'AB', label: 'Alberta' }, { value: 'BC', label: 'British Columbia' },
      { value: 'MB', label: 'Manitoba' }, { value: 'NB', label: 'New Brunswick' },
      { value: 'NL', label: 'Newfoundland and Labrador' }, { value: 'NS', label: 'Nova Scotia' },
      { value: 'ON', label: 'Ontario' }, { value: 'PE', label: 'Prince Edward Island' },
      { value: 'QC', label: 'Quebec' }, { value: 'SK', label: 'Saskatchewan' },
      { value: 'NT', label: 'Northwest Territories' }, { value: 'NU', label: 'Nunavut' },
      { value: 'YT', label: 'Yukon' },
    ],
    postalLabel: 'Postal Code',
    postalPlaceholder: 'K1A 0A6',
    currency: 'CAD',
    taxIdFields: [
      { key: 'businessNumber', label: 'Business Number (BN)', placeholder: '123456789' },
      { key: 'gstNumber', label: 'GST/HST Number', placeholder: '123456789RT0001' },
      { key: 'qstNumber', label: 'PST/QST Number', placeholder: '1234567890TQ0001' },
      { key: 'sin', label: 'Social Insurance Number (SIN)', placeholder: '123-456-789', individualOnly: true },
    ],
  },
  india: {
    regionLabel: 'State',
    regions: [
      { value: 'AP', label: 'Andhra Pradesh' }, { value: 'AR', label: 'Arunachal Pradesh' },
      { value: 'AS', label: 'Assam' }, { value: 'BR', label: 'Bihar' },
      { value: 'CT', label: 'Chhattisgarh' }, { value: 'GA', label: 'Goa' },
      { value: 'GJ', label: 'Gujarat' }, { value: 'HR', label: 'Haryana' },
      { value: 'HP', label: 'Himachal Pradesh' }, { value: 'JH', label: 'Jharkhand' },
      { value: 'KA', label: 'Karnataka' }, { value: 'KL', label: 'Kerala' },
      { value: 'MP', label: 'Madhya Pradesh' }, { value: 'MH', label: 'Maharashtra' },
      { value: 'MN', label: 'Manipur' }, { value: 'ML', label: 'Meghalaya' },
      { value: 'MZ', label: 'Mizoram' }, { value: 'NL', label: 'Nagaland' },
      { value: 'OR', label: 'Odisha' }, { value: 'PB', label: 'Punjab' },
      { value: 'RJ', label: 'Rajasthan' }, { value: 'SK', label: 'Sikkim' },
      { value: 'TN', label: 'Tamil Nadu' }, { value: 'TG', label: 'Telangana' },
      { value: 'TR', label: 'Tripura' }, { value: 'UP', label: 'Uttar Pradesh' },
      { value: 'UT', label: 'Uttarakhand' }, { value: 'WB', label: 'West Bengal' },
      { value: 'DL', label: 'Delhi' }, { value: 'JK', label: 'Jammu and Kashmir' },
      { value: 'PY', label: 'Puducherry' },
    ],
    postalLabel: 'PIN Code',
    postalPlaceholder: '110001',
    currency: 'INR',
    taxIdFields: [
      { key: 'businessNumber', label: 'PAN (Permanent Account Number)', placeholder: 'ABCDE1234F' },
      { key: 'gstNumber', label: 'GSTIN (GST Identification Number)', placeholder: '22AAAAA0000A1Z5' },
      { key: 'qstNumber', label: 'TAN (Tax Deduction Account Number)', placeholder: 'DELE12345F' },
      { key: 'sin', label: 'Aadhaar Number', placeholder: '1234 5678 9012', individualOnly: true },
    ],
  },
  usa: {
    regionLabel: 'State',
    regions: [
      { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' }, { value: 'AZ', label: 'Arizona' },
      { value: 'AR', label: 'Arkansas' }, { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
      { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' }, { value: 'FL', label: 'Florida' },
      { value: 'GA', label: 'Georgia' }, { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
      { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' }, { value: 'IA', label: 'Iowa' },
      { value: 'KS', label: 'Kansas' }, { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
      { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' }, { value: 'MA', label: 'Massachusetts' },
      { value: 'MI', label: 'Michigan' }, { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
      { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' }, { value: 'NE', label: 'Nebraska' },
      { value: 'NV', label: 'Nevada' }, { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
      { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' }, { value: 'NC', label: 'North Carolina' },
      { value: 'ND', label: 'North Dakota' }, { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
      { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' }, { value: 'RI', label: 'Rhode Island' },
      { value: 'SC', label: 'South Carolina' }, { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
      { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' }, { value: 'VT', label: 'Vermont' },
      { value: 'VA', label: 'Virginia' }, { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
      { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' }, { value: 'DC', label: 'District of Columbia' },
    ],
    postalLabel: 'ZIP Code',
    postalPlaceholder: '10001',
    currency: 'USD',
    taxIdFields: [
      { key: 'businessNumber', label: 'EIN (Employer Identification Number)', placeholder: '12-3456789' },
      { key: 'gstNumber', label: 'State Tax ID', placeholder: 'State-dependent' },
      { key: 'sin', label: 'SSN (Social Security Number)', placeholder: '123-45-6789', individualOnly: true },
    ],
  },
  uae: {
    regionLabel: 'Emirate',
    regions: [
      { value: 'AUH', label: 'Abu Dhabi' }, { value: 'AJM', label: 'Ajman' },
      { value: 'DXB', label: 'Dubai' }, { value: 'FUJ', label: 'Fujairah' },
      { value: 'RKH', label: 'Ras al-Khaimah' }, { value: 'SHJ', label: 'Sharjah' },
      { value: 'UAQ', label: 'Umm al-Quwain' },
    ],
    postalLabel: 'Postal Code',
    postalPlaceholder: '12345',
    currency: 'AED',
    taxIdFields: [
      { key: 'businessNumber', label: 'TRN (Tax Registration Number)', placeholder: '100123456700003' },
      { key: 'gstNumber', label: 'VAT Registration Number', placeholder: '100123456700003' },
      { key: 'qstNumber', label: 'Trade License Number', placeholder: '123456' },
    ],
  },
  germany: {
    regionLabel: 'State',
    regions: [
      { value: 'BW', label: 'Baden-Württemberg' }, { value: 'BY', label: 'Bavaria' },
      { value: 'BE', label: 'Berlin' }, { value: 'BB', label: 'Brandenburg' },
      { value: 'HB', label: 'Bremen' }, { value: 'HH', label: 'Hamburg' },
      { value: 'HE', label: 'Hesse' }, { value: 'MV', label: 'Mecklenburg-Vorpommern' },
      { value: 'NI', label: 'Lower Saxony' }, { value: 'NW', label: 'North Rhine-Westphalia' },
      { value: 'RP', label: 'Rhineland-Palatinate' }, { value: 'SL', label: 'Saarland' },
      { value: 'SN', label: 'Saxony' }, { value: 'ST', label: 'Saxony-Anhalt' },
      { value: 'SH', label: 'Schleswig-Holstein' }, { value: 'TH', label: 'Thuringia' },
    ],
    postalLabel: 'Postal Code',
    postalPlaceholder: '10115',
    currency: 'EUR',
    taxIdFields: [
      { key: 'businessNumber', label: 'Steuernummer (Tax Number)', placeholder: '123/456/78901' },
      { key: 'gstNumber', label: 'USt-IdNr. (VAT ID)', placeholder: 'DE123456789' },
      { key: 'qstNumber', label: 'Handelsregisternummer (Commercial Reg.)', placeholder: 'HRB 12345' },
      { key: 'sin', label: 'Steueridentifikationsnummer', placeholder: '12345678901', individualOnly: true },
    ],
  },
  france: {
    regionLabel: 'Region',
    regions: [
      { value: 'ARA', label: 'Auvergne-Rhône-Alpes' }, { value: 'BFC', label: 'Bourgogne-Franche-Comté' },
      { value: 'BRE', label: 'Brittany' }, { value: 'CVL', label: 'Centre-Val de Loire' },
      { value: 'COR', label: 'Corsica' }, { value: 'GES', label: 'Grand Est' },
      { value: 'HDF', label: 'Hauts-de-France' }, { value: 'IDF', label: 'Île-de-France' },
      { value: 'NOR', label: 'Normandy' }, { value: 'NAQ', label: 'Nouvelle-Aquitaine' },
      { value: 'OCC', label: 'Occitanie' }, { value: 'PDL', label: 'Pays de la Loire' },
      { value: 'PAC', label: "Provence-Alpes-Côte d'Azur" },
    ],
    postalLabel: 'Postal Code',
    postalPlaceholder: '75001',
    currency: 'EUR',
    taxIdFields: [
      { key: 'businessNumber', label: 'SIREN / SIRET', placeholder: '123 456 789 00012' },
      { key: 'gstNumber', label: 'N° TVA Intracommunautaire', placeholder: 'FR12345678901' },
      { key: 'sin', label: 'N° de Sécurité Sociale', placeholder: '1 85 05 78 300 270 40', individualOnly: true },
    ],
  },
  australia: {
    regionLabel: 'State / Territory',
    regions: [
      { value: 'ACT', label: 'Australian Capital Territory' }, { value: 'NSW', label: 'New South Wales' },
      { value: 'NT', label: 'Northern Territory' }, { value: 'QLD', label: 'Queensland' },
      { value: 'SA', label: 'South Australia' }, { value: 'TAS', label: 'Tasmania' },
      { value: 'VIC', label: 'Victoria' }, { value: 'WA', label: 'Western Australia' },
    ],
    postalLabel: 'Postcode',
    postalPlaceholder: '2000',
    currency: 'AUD',
    taxIdFields: [
      { key: 'businessNumber' as TaxIdKey, label: 'ABN (Australian Business Number)', placeholder: '51 824 753 556' },
      { key: 'gstNumber' as TaxIdKey, label: 'ACN (Australian Company Number)', placeholder: '123 456 789' },
      { key: 'qstNumber' as TaxIdKey, label: 'GST Registration Number', placeholder: '123456789' },
      { key: 'sin' as TaxIdKey, label: 'TFN (Tax File Number)', placeholder: '123 456 789', individualOnly: true },
    ],
  },
};

const DEFAULT_COUNTRY_CONFIG = {
  regionLabel: 'State / Region',
  regions: [] as { value: string; label: string }[],
  postalLabel: 'Postal Code',
  postalPlaceholder: '',
  currency: 'USD',
  taxIdFields: [
    { key: 'businessNumber' as TaxIdKey, label: 'Business Registration Number', placeholder: '' },
    { key: 'gstNumber' as TaxIdKey, label: 'Tax Number', placeholder: '' },
  ] as TaxIdField[],
};

// Types
interface Client {
  id: string;
  displayName: string;
  legalName?: string;
  clientType: 'individual' | 'corporation' | 'partnership' | 'trust' | 'other';
  contact: {
    email?: string;
    phone?: string;
    fax?: string;
    website?: string;
  };
  address: {
    street?: string;
    city?: string;
    province?: string;
    postalCode?: string;
    country?: string;
  };
  taxIds: {
    businessNumber?: string;
    gstNumber?: string;
    qstNumber?: string;
    sin?: string;
  };
  preferredCurrency?: string;
  notes?: string;
  isActive: boolean;
  fullAddress: string;
  createdAt: string;
  updatedAt: string;
}

interface ClientFormData {
  displayName: string;
  legalName?: string;
  clientType: 'individual' | 'corporation' | 'partnership' | 'trust' | 'other';
  contact: {
    email?: string;
    phone?: string;
    fax?: string;
    website?: string;
  };
  address: {
    street?: string;
    city?: string;
    province?: string;
    postalCode?: string;
    country?: string;
  };
  taxIds: {
    businessNumber?: string;
    gstNumber?: string;
    qstNumber?: string;
    sin?: string;
  };
  preferredCurrency?: string;
  notes?: string;
  isActive: boolean;
}

interface ClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ClientFormData) => void;
  client?: Client | null;
  isLoading?: boolean;
  title: string;
}

const initialFormData: ClientFormData = {
  displayName: '',
  legalName: '',
  clientType: 'individual',
  contact: {
    email: '',
    phone: '',
    fax: '',
    website: '',
  },
  address: {
    street: '',
    city: '',
    province: '',
    postalCode: '',
    country: 'canada',
  },
  taxIds: {
    businessNumber: '',
    gstNumber: '',
    qstNumber: '',
    sin: '',
  },
  preferredCurrency: 'CAD',
  notes: '',
  isActive: true,
};

export const ClientModal: React.FC<ClientModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  client,
  isLoading = false,
  title,
}) => {
  const [formData, setFormData] = useState<ClientFormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [countries, setCountries] = useState<Country[]>([]);
  const [countriesLoading, setCountriesLoading] = useState(false);

  useEffect(() => {
    const loadCountries = async () => {
      try {
        setCountriesLoading(true);
        const response = await countryService.getSupportedCountries();
        setCountries(response.data);
      } catch (error) {
        console.error('Failed to load countries:', error);
      } finally {
        setCountriesLoading(false);
      }
    };
    loadCountries();
  }, []);

  useEffect(() => {
    if (client) {
      setFormData({
        displayName: client.displayName,
        legalName: client.legalName || '',
        clientType: client.clientType,
        contact: {
          email: client.contact.email || '',
          phone: client.contact.phone || '',
          fax: client.contact.fax || '',
          website: client.contact.website || '',
        },
        address: {
          street: client.address.street || '',
          city: client.address.city || '',
          province: client.address.province || '',
          postalCode: client.address.postalCode || '',
          country: client.address.country || 'canada',
        },
        taxIds: {
          businessNumber: client.taxIds.businessNumber || '',
          gstNumber: client.taxIds.gstNumber || '',
          qstNumber: client.taxIds.qstNumber || '',
          sin: client.taxIds.sin || '',
        },
        preferredCurrency: client.preferredCurrency || 'CAD',
        notes: client.notes || '',
        isActive: client.isActive,
      });
    } else {
      setFormData(initialFormData);
    }
    setErrors({});
  }, [client, isOpen]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.displayName.trim()) {
      newErrors.displayName = 'Display name is required';
    }

    if (formData.contact.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (formData.contact.website && !formData.contact.website.match(/^https?:\/\/.+/)) {
      newErrors.website = 'Website must start with http:// or https://';
    }

    if (formData.address.postalCode) {
      const country = formData.address.country || 'canada';
      const postalValidations: Record<string, { regex: RegExp; hint: string }> = {
        canada: { regex: /^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/, hint: 'e.g., K1A 0A6' },
        usa:    { regex: /^\d{5}(-\d{4})?$/, hint: 'e.g., 10001' },
        india:  { regex: /^\d{6}$/, hint: 'e.g., 110001' },
        australia: { regex: /^\d{4}$/, hint: 'e.g., 2000' },
        germany: { regex: /^\d{5}$/, hint: 'e.g., 10115' },
        france:  { regex: /^\d{5}$/, hint: 'e.g., 75001' },
      };
      const v = postalValidations[country];
      if (v && !v.regex.test(formData.address.postalCode)) {
        const cc = COUNTRY_CONFIG[country] || DEFAULT_COUNTRY_CONFIG;
        newErrors.postalCode = `Please enter a valid ${cc.postalLabel} (${v.hint})`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    const keys = field.split('.');
    if (keys.length === 1) {
      // Handle boolean conversion for isActive field
      const processedValue = field === 'isActive' ? value === 'true' : value;
      setFormData(prev => ({ ...prev, [field]: processedValue }));
    } else if (keys.length === 2) {
      setFormData(prev => ({
        ...prev,
        [keys[0]]: {
          ...prev[keys[0] as keyof ClientFormData] as any,
          [keys[1]]: value,
        },
      }));
    }
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleCountryChange = (newCountry: string) => {
    const config = COUNTRY_CONFIG[newCountry] || DEFAULT_COUNTRY_CONFIG;
    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        country: newCountry,
        province: '',
        postalCode: '',
      },
      preferredCurrency: config.currency,
      taxIds: {
        businessNumber: '',
        gstNumber: '',
        qstNumber: '',
        sin: '',
      },
    }));
    setErrors(prev => ({ ...prev, postalCode: '' }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Building2 className="w-5 h-5 mr-2" />
              Basic Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Name *
                </label>
                <input
                  type="text"
                  value={formData.displayName}
                  onChange={(e) => handleInputChange('displayName', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.displayName ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter display name"
                />
                {errors.displayName && (
                  <p className="text-red-500 text-sm mt-1">{errors.displayName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Legal Name
                </label>
                <input
                  type="text"
                  value={formData.legalName}
                  onChange={(e) => handleInputChange('legalName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter legal name (if different)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Client Type *
                </label>
                <select
                  value={formData.clientType}
                  onChange={(e) => handleInputChange('clientType', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="individual">Individual</option>
                  <option value="corporation">Corporation</option>
                  <option value="partnership">Partnership</option>
                  <option value="trust">Trust</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={formData.isActive ? 'active' : 'inactive'}
                  onChange={(e) => handleInputChange('isActive', e.target.value === 'active' ? 'true' : 'false')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Preferred Currency
                </label>
                <select
                  value={formData.preferredCurrency || 'CAD'}
                  onChange={(e) => handleInputChange('preferredCurrency', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="CAD">Canadian Dollar (CAD)</option>
                  <option value="USD">US Dollar (USD)</option>
                  <option value="INR">Indian Rupee (INR)</option>
                  <option value="AED">UAE Dirham (AED)</option>
                  <option value="EUR">Euro (EUR)</option>
                  <option value="GBP">British Pound (GBP)</option>
                  <option value="AUD">Australian Dollar (AUD)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Phone className="w-5 h-5 mr-2" />
              Contact Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.contact.email}
                  onChange={(e) => handleInputChange('contact.email', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.email ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter email address"
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.contact.phone}
                  onChange={(e) => handleInputChange('contact.phone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter phone number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fax
                </label>
                <input
                  type="tel"
                  value={formData.contact.fax}
                  onChange={(e) => handleInputChange('contact.fax', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter fax number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Website
                </label>
                <input
                  type="url"
                  value={formData.contact.website}
                  onChange={(e) => handleInputChange('contact.website', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.website ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="https://example.com"
                />
                {errors.website && (
                  <p className="text-red-500 text-sm mt-1">{errors.website}</p>
                )}
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <MapPin className="w-5 h-5 mr-2" />
              Address Information
            </h3>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Street Address
                </label>
                <input
                  type="text"
                  value={formData.address.street}
                  onChange={(e) => handleInputChange('address.street', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter street address"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    value={formData.address.city}
                    onChange={(e) => handleInputChange('address.city', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter city"
                  />
                </div>

<div>
                  {(() => {
                    const cc = formData.address.country ? (COUNTRY_CONFIG[formData.address.country] || DEFAULT_COUNTRY_CONFIG) : DEFAULT_COUNTRY_CONFIG;
                    return (
                      <>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {cc.regionLabel}
                        </label>
                        {cc.regions.length > 0 ? (
                          <select
                            value={formData.address.province}
                            onChange={(e) => handleInputChange('address.province', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          >
                            <option value="">Select {cc.regionLabel}</option>
                            {cc.regions.map(r => (
                              <option key={r.value} value={r.value}>{r.label}</option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type="text"
                            value={formData.address.province}
                            onChange={(e) => handleInputChange('address.province', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder={`Enter ${cc.regionLabel.toLowerCase()}`}
                          />
                        )}
                      </>
                    );
                  })()}
                </div>

<div>
                  {(() => {
                    const cc = formData.address.country ? (COUNTRY_CONFIG[formData.address.country] || DEFAULT_COUNTRY_CONFIG) : DEFAULT_COUNTRY_CONFIG;
                    return (
                      <>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {cc.postalLabel}
                        </label>
                        <input
                          type="text"
                          value={formData.address.postalCode}
                          onChange={(e) => handleInputChange('address.postalCode', e.target.value.toUpperCase())}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                            errors.postalCode ? 'border-red-300' : 'border-gray-300'
                          }`}
                          placeholder={cc.postalPlaceholder || 'Enter postal code'}
                        />
                        {errors.postalCode && (
                          <p className="text-red-500 text-sm mt-1">{errors.postalCode}</p>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Country
                </label>
                <select
                  value={formData.address.country}
                  onChange={(e) => handleCountryChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  disabled={countriesLoading}
                >
                  <option value="">{countriesLoading ? 'Loading...' : 'Select a country'}</option>
                  {countries.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Tax IDs */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Hash className="w-5 h-5 mr-2" />
              Tax Identification Numbers
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(COUNTRY_CONFIG[formData.address.country || 'canada'] || DEFAULT_COUNTRY_CONFIG).taxIdFields.map((field) => {
                if (field.individualOnly && formData.clientType !== 'individual') return null;
                return (
                  <div key={field.key}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {field.label}
                    </label>
                    <input
                      type="text"
                      value={formData.taxIds[field.key] || ''}
                      onChange={(e) => handleInputChange(`taxIds.${field.key}`, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder={field.placeholder}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Notes */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <FileText className="w-5 h-5 mr-2" />
              Notes
            </h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Additional Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Enter any additional notes about this client..."
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Saving...' : client ? 'Update Client' : 'Create Client'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Client View Modal Component
interface ClientViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: Client | null;
  onEdit: () => void;
}

export const ClientViewModal: React.FC<ClientViewModalProps> = ({
  isOpen,
  onClose,
  client,
  onEdit,
}) => {
  if (!isOpen || !client) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Client Details</h2>
          <div className="flex items-center space-x-2">
            <button
              onClick={onEdit}
              className="px-3 py-1 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm"
            >
              Edit
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Building2 className="w-5 h-5 mr-2" />
              Basic Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
                <p className="text-sm text-gray-900">{client.displayName}</p>
              </div>
              {client.legalName && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Legal Name</label>
                  <p className="text-sm text-gray-900">{client.legalName}</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client Type</label>
                <p className="text-sm text-gray-900 capitalize">{client.clientType}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  client.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {client.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Currency</label>
                <p className="text-sm text-gray-900">
                  {client.preferredCurrency === 'USD' ? 'US Dollar (USD)' : 'Canadian Dollar (CAD)'}
                </p>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Phone className="w-5 h-5 mr-2" />
              Contact Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {client.contact.email && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <p className="text-sm text-gray-900">{client.contact.email}</p>
                </div>
              )}
              {client.contact.phone && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <p className="text-sm text-gray-900">{client.contact.phone}</p>
                </div>
              )}
              {client.contact.fax && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fax</label>
                  <p className="text-sm text-gray-900">{client.contact.fax}</p>
                </div>
              )}
              {client.contact.website && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                  <a
                    href={client.contact.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary-600 hover:text-primary-700"
                  >
                    {client.contact.website}
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Address Information */}
          {client.fullAddress && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <MapPin className="w-5 h-5 mr-2" />
                Address Information
              </h3>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Address</label>
                  <p className="text-sm text-gray-900">{client.fullAddress}</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {client.address.city && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                      <p className="text-sm text-gray-900">{client.address.city}</p>
                    </div>
                  )}
                  {client.address.province && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Province</label>
                      <p className="text-sm text-gray-900">{client.address.province}</p>
                    </div>
                  )}
                  {client.address.postalCode && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code</label>
                      <p className="text-sm text-gray-900">{client.address.postalCode}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Tax IDs */}
          {(client.taxIds.businessNumber || client.taxIds.gstNumber || client.taxIds.qstNumber || client.taxIds.sin) && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <Hash className="w-5 h-5 mr-2" />
                Tax Identification Numbers
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {client.taxIds.businessNumber && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Business Number</label>
                    <p className="text-sm text-gray-900">{client.taxIds.businessNumber}</p>
                  </div>
                )}
                {client.taxIds.gstNumber && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">GST/HST Number</label>
                    <p className="text-sm text-gray-900">{client.taxIds.gstNumber}</p>
                  </div>
                )}
                {client.taxIds.qstNumber && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">QST Number</label>
                    <p className="text-sm text-gray-900">{client.taxIds.qstNumber}</p>
                  </div>
                )}
                {client.taxIds.sin && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">SIN</label>
                    <p className="text-sm text-gray-900">{client.taxIds.sin}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          {client.notes && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <FileText className="w-5 h-5 mr-2" />
                Notes
              </h3>
              <p className="text-sm text-gray-900 whitespace-pre-wrap">{client.notes}</p>
            </div>
          )}

          {/* Metadata */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-4">System Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Created</label>
                <p className="text-sm text-gray-900">{new Date(client.createdAt).toLocaleString()}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Updated</label>
                <p className="text-sm text-gray-900">{new Date(client.updatedAt).toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Client Import Modal Component
interface ClientImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (clients: ClientFormData[]) => void;
  isLoading?: boolean;
}

// Import Results Modal Component
interface ImportResult {
  successful: any[];
  failed: Array<{ client: ClientFormData; error: string }>;
  total: number;
}

interface ClientImportResultsModalProps {
  isOpen: boolean;
  onClose: () => void;
  results: ImportResult | null;
}

export const ClientImportResultsModal: React.FC<ClientImportResultsModalProps> = ({
  isOpen,
  onClose,
  results,
}) => {
  if (!isOpen || !results) return null;

  const successCount = results.successful.length;
  const failureCount = results.failed.length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Building2 className="w-6 h-6 text-primary-600" />
            <h2 className="text-xl font-semibold text-gray-900">Import Results</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Summary */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium">Total Records</p>
                  <p className="text-2xl font-bold text-blue-900">{results.total}</p>
                </div>
                <Building2 className="w-8 h-8 text-blue-400" />
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-medium">Successfully Imported</p>
                  <p className="text-2xl font-bold text-green-900">{successCount}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-600 font-medium">Failed</p>
                  <p className="text-2xl font-bold text-red-900">{failureCount}</p>
                </div>
                <XCircle className="w-8 h-8 text-red-400" />
              </div>
            </div>
          </div>

          {/* Successful Imports */}
          {successCount > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                Successfully Imported ({successCount})
              </h3>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-green-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-gray-700">Display Name</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-700">Type</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-700">Email</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-700">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {results.successful.map((client, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-2 font-medium text-gray-900">{client.displayName}</td>
                          <td className="px-4 py-2 capitalize text-gray-600">{client.clientType}</td>
                          <td className="px-4 py-2 text-gray-600">{client.contact?.email || '-'}</td>
                          <td className="px-4 py-2">
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                              Active
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Failed Imports */}
          {failureCount > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <XCircle className="w-5 h-5 text-red-600 mr-2" />
                Failed Imports ({failureCount})
              </h3>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-red-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-gray-700">Display Name</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-700">Type</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-700">Email</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-700">Reason for Failure</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {results.failed.map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-2 font-medium text-gray-900">{item.client.displayName}</td>
                          <td className="px-4 py-2 capitalize text-gray-600">{item.client.clientType}</td>
                          <td className="px-4 py-2 text-gray-600">{item.client.contact?.email || '-'}</td>
                          <td className="px-4 py-2">
                            <span className="inline-flex px-2 py-1 text-xs font-medium rounded bg-red-100 text-red-800 max-w-xs">
                              {item.error}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export const ClientImportModal: React.FC<ClientImportModalProps> = ({
  isOpen,
  onClose,
  onImport,
  isLoading = false,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<ClientFormData[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [step, setStep] = useState<'upload' | 'preview' | 'importing'>('upload');

  useEffect(() => {
    if (!isOpen) {
      setFile(null);
      setParsedData([]);
      setErrors([]);
      setStep('upload');
    }
  }, [isOpen]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      parseCSVFile(selectedFile);
    }
  };

  const parseCSVFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      try {
        const parsed = parseCSV(text);
        setParsedData(parsed.data);
        setErrors(parsed.errors);
        setStep('preview');
      } catch (error) {
        setErrors(['Failed to parse CSV file. Please check the format.']);
      }
    };
    reader.readAsText(file);
  };

  const parseCSV = (text: string): { data: ClientFormData[]; errors: string[] } => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      throw new Error('CSV must have at least a header row and one data row');
    }

    const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim().toLowerCase());
    const data: ClientFormData[] = [];
    const errors: string[] = [];

    // Expected headers mapping
    const headerMap: Record<string, string> = {
      'display name': 'displayName',
      'legal name': 'legalName',
      'client type': 'clientType',
      'email': 'contact.email',
      'phone': 'contact.phone',
      'fax': 'contact.fax',
      'website': 'contact.website',
      'street': 'address.street',
      'city': 'address.city',
      'province': 'address.province',
      'postal code': 'address.postalCode',
      'country': 'address.country',
      'business number': 'taxIds.businessNumber',
      'gst number': 'taxIds.gstNumber',
      'qst number': 'taxIds.qstNumber',
      'sin': 'taxIds.sin',
      'preferred currency': 'preferredCurrency',
      'notes': 'notes',
      'status': 'isActive'
    };

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.replace(/"/g, '').trim());

      if (values.length !== headers.length) {
        errors.push(`Row ${i + 1}: Column count mismatch`);
        continue;
      }

      const client: ClientFormData = {
        displayName: '',
        clientType: 'individual',
        contact: {},
        address: { country: 'Canada' },
        taxIds: {},
        preferredCurrency: 'CAD',
        isActive: true,
      };

      let hasDisplayName = false;

      headers.forEach((header, index) => {
        const mappedField = headerMap[header];
        const value = values[index];

        if (!mappedField || !value) return;

        if (mappedField === 'displayName') {
          client.displayName = value;
          hasDisplayName = true;
        } else if (mappedField === 'legalName') {
          client.legalName = value;
        } else if (mappedField === 'clientType') {
          const validTypes = ['individual', 'corporation', 'partnership', 'trust', 'other'];
          client.clientType = validTypes.includes(value.toLowerCase())
            ? value.toLowerCase() as any
            : 'individual';
        } else if (mappedField.startsWith('contact.')) {
          const field = mappedField.split('.')[1];
          client.contact[field as keyof typeof client.contact] = value;
        } else if (mappedField.startsWith('address.')) {
          const field = mappedField.split('.')[1];
          client.address[field as keyof typeof client.address] = value;
        } else if (mappedField.startsWith('taxIds.')) {
          const field = mappedField.split('.')[1];
          client.taxIds[field as keyof typeof client.taxIds] = value;
        } else if (mappedField === 'preferredCurrency') {
          const validCurrencies = ['CAD', 'USD'];
          client.preferredCurrency = validCurrencies.includes(value.toUpperCase())
            ? value.toUpperCase() as 'CAD' | 'USD'
            : 'CAD';
        } else if (mappedField === 'notes') {
          client.notes = value;
        } else if (mappedField === 'isActive') {
          client.isActive = ['true', 'active', '1', 'yes'].includes(value.toLowerCase());
        }
      });

      if (!hasDisplayName) {
        errors.push(`Row ${i + 1}: Display name is required`);
        continue;
      }

      data.push(client);
    }

    return { data, errors };
  };

  const handleImport = () => {
    if (parsedData.length > 0) {
      setStep('importing');
      onImport(parsedData);
    }
  };

  const downloadTemplate = () => {
    const template = [
      'Display Name,Legal Name,Client Type,Email,Phone,Fax,Website,Street,City,Province,Postal Code,Country,Business Number,GST Number,QST Number,SIN,Notes,Status',
      'Sample Corp,Sample Corporation Ltd,corporation,contact@sample.com,555-123-4567,,https://sample.com,123 Main St,Toronto,ON,M5V 3A8,Canada,123456789,123456789RT0001,,,Sample client for testing,Active',
      'John Doe,,individual,john@example.com,555-987-6543,,,456 Oak Ave,Vancouver,BC,V6B 1A1,Canada,,,987654321,Personal client,Active'
    ].join('\n');

    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'client-import-template.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Import Clients</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {step === 'upload' && (
            <div className="space-y-6">
              <div className="text-center">
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Upload CSV File</h3>
                <p className="text-gray-600 mb-4">
                  Upload a CSV file with client data. Make sure your file includes the required columns.
                </p>
              </div>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="hidden"
                  id="csv-upload"
                />
                <label
                  htmlFor="csv-upload"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <Upload className="w-8 h-8 text-gray-400 mb-2" />
                  <span className="text-sm font-medium text-gray-900">Click to upload CSV file</span>
                  <span className="text-xs text-gray-500">or drag and drop</span>
                </label>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 mr-2" />
                  <div>
                    <h4 className="text-sm font-medium text-blue-900">CSV Format Requirements</h4>
                    <ul className="text-sm text-blue-800 mt-1 list-disc list-inside">
                      <li>First row must contain column headers</li>
                      <li>Display Name column is required</li>
                      <li>Client Type must be: individual, corporation, partnership, trust, or other</li>
                      <li>Status can be: Active, Inactive, True, False, 1, 0, Yes, No</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={downloadTemplate}
                  className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Template
                </button>
              </div>
            </div>
          )}

          {step === 'preview' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Preview Import Data</h3>
                <p className="text-gray-600">
                  Review the parsed data before importing. {parsedData.length} clients will be imported.
                </p>
              </div>

              {errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-2" />
                    <div>
                      <h4 className="text-sm font-medium text-red-900">Import Errors</h4>
                      <ul className="text-sm text-red-800 mt-1">
                        {errors.map((error, index) => (
                          <li key={index}>• {error}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="max-h-96 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left">Display Name</th>
                        <th className="px-4 py-2 text-left">Type</th>
                        <th className="px-4 py-2 text-left">Email</th>
                        <th className="px-4 py-2 text-left">Phone</th>
                        <th className="px-4 py-2 text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {parsedData.slice(0, 10).map((client, index) => (
                        <tr key={index}>
                          <td className="px-4 py-2">{client.displayName}</td>
                          <td className="px-4 py-2 capitalize">{client.clientType}</td>
                          <td className="px-4 py-2">{client.contact.email || '-'}</td>
                          <td className="px-4 py-2">{client.contact.phone || '-'}</td>
                          <td className="px-4 py-2">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              client.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {client.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {parsedData.length > 10 && (
                    <div className="px-4 py-2 text-sm text-gray-500 bg-gray-50">
                      ... and {parsedData.length - 10} more clients
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setStep('upload')}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleImport}
                  disabled={parsedData.length === 0 || isLoading}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Importing...' : `Import ${parsedData.length} Clients`}
                </button>
              </div>
            </div>
          )}

          {step === 'importing' && (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Importing Clients</h3>
              <p className="text-gray-600">Please wait while we import your clients...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
